// Formatter API Service
// Handles multi-platform content formatting (Xiaohongshu, WeChat)

import { API_BASE_URL } from '../config/api';

/**
 * Format content for Xiaohongshu
 * @param {Object} content - Content to format
 * @param {string} token - Auth token
 * @returns {Promise<Object>} - Formatted content for Xiaohongshu
 */
export const formatForXiaohongshu = async (content, token) => {
    const response = await fetch(`${API_BASE_URL}/api/v1/formatters/xiaohongshu/`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(content)
    });

    if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || '小红书格式化失败');
    }

    return response.json();
};

/**
 * Format content for WeChat Official Account
 * @param {Object} content - Content to format
 * @param {string} theme - Style theme (tech/lifestyle/business)
 * @param {string} token - Auth token
 * @returns {Promise<Object>} - Formatted HTML content for WeChat
 */
export const formatForWechat = async (content, theme = 'tech', token) => {
    const response = await fetch(`${API_BASE_URL}/api/v1/formatters/wechat/`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ ...content, theme })
    });

    if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || '公众号格式化失败');
    }

    return response.json();
};

/**
 * Get available WeChat themes
 * @returns {Array} - List of available themes
 */
export const getWechatThemes = () => [
    { id: 'tech', name: '科技风', description: '深色强调，适合技术类文章', accent: '#6366f1' },
    { id: 'lifestyle', name: '生活风', description: '温暖柔和，适合生活分享', accent: '#ec4899' },
    { id: 'business', name: '商务风', description: '专业稳重，适合商业内容', accent: '#059669' }
];

// Platform format constraints
export const PLATFORM_LIMITS = {
    xiaohongshu: {
        titleMaxLength: 20,
        descMaxLength: 1000,
        minImages: 1,
        maxImages: 9,
        hashtagsMax: 10
    },
    wechat: {
        titleMaxLength: 64,
        summaryMaxLength: 120,
        imageMaxWidth: 1080
    }
};

/**
 * Validate content against platform limits
 * @param {Object} content - Content to validate
 * @param {string} platform - Target platform
 * @returns {Object} - Validation result with errors
 */
export const validateContent = (content, platform) => {
    const limits = PLATFORM_LIMITS[platform];
    const errors = [];

    if (!limits) {
        return { valid: false, errors: ['不支持的平台'] };
    }

    if (platform === 'xiaohongshu') {
        if (content.title && content.title.length > limits.titleMaxLength) {
            errors.push(`标题不能超过${limits.titleMaxLength}字`);
        }
        if (content.description && content.description.length > limits.descMaxLength) {
            errors.push(`正文不能超过${limits.descMaxLength}字`);
        }
        if (content.images && content.images.length > limits.maxImages) {
            errors.push(`图片数量不能超过${limits.maxImages}张`);
        }
    }

    if (platform === 'wechat') {
        if (content.title && content.title.length > limits.titleMaxLength) {
            errors.push(`标题不能超过${limits.titleMaxLength}字`);
        }
    }

    return {
        valid: errors.length === 0,
        errors
    };
};

/**
 * Copy formatted text to clipboard
 * @param {string} text - Text to copy
 * @returns {Promise<boolean>} - Success status
 */
export const copyToClipboard = async (text) => {
    try {
        await navigator.clipboard.writeText(text);
        return true;
    } catch (err) {
        // Fallback for older browsers
        const textarea = document.createElement('textarea');
        textarea.value = text;
        textarea.style.position = 'fixed';
        textarea.style.left = '-9999px';
        document.body.appendChild(textarea);
        textarea.select();
        try {
            document.execCommand('copy');
            return true;
        } catch (e) {
            return false;
        } finally {
            document.body.removeChild(textarea);
        }
    }
};

/**
 * Download images as zip
 * @param {Array} imageUrls - URLs of images to download
 * @param {string} filename - Zip file name
 */
export const downloadImages = async (imageUrls, filename = 'images') => {
    // For single image, direct download
    if (imageUrls.length === 1) {
        const link = document.createElement('a');
        link.href = imageUrls[0];
        link.download = `${filename}_1.png`;
        link.click();
        return;
    }

    // For multiple images, download one by one
    imageUrls.forEach((url, index) => {
        setTimeout(() => {
            const link = document.createElement('a');
            link.href = url;
            link.download = `${filename}_${index + 1}.png`;
            link.click();
        }, index * 500);
    });
};

const formatterService = {
    formatForXiaohongshu,
    formatForWechat,
    getWechatThemes,
    PLATFORM_LIMITS,
    validateContent,
    copyToClipboard,
    downloadImages
};

export default formatterService;
